/**
 * Versioned release guard (no service worker).
 *
 * Project policy is fresh-build-only: no PWA service worker is ever registered.
 * Instead, every build stamps a build id into `index.html` and into a tiny
 * `/version.json` manifest. This module:
 *   1. unregisters any legacy service worker and purges Cache Storage,
 *   2. polls `/version.json` (focus + interval, always `no-store`) to detect a
 *      newer deploy while a tab is open,
 *   3. notifies subscribers so the UI can offer "Reload for the latest version",
 *      and auto-reloads when the tab is idle/hidden or navigation chunks 404,
 *   4. recovers from stale route chunks (`vite:preloadError`, failed dynamic
 *      imports) by purging and hard-reloading once.
 */

declare const __BUILD_ID__: string | undefined;

const BUILD_ID: string = String(
  (import.meta.env?.VITE_BUILD_ID as string | undefined) ??
    (typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev"),
);

const STORAGE_KEY = "gg-build-id";
const RELOAD_GUARD_KEY = "gg-build-reloaded-for";
const CHECK_INTERVAL_MS = 60_000;
const POLL_INTERVAL_MS = 5 * 60_000;

let lastCheckedAt = 0;
let pendingBuildId: string | null = null;
const listeners = new Set<(buildId: string) => void>();

export function getBuildId(): string {
  return BUILD_ID;
}

/** Build id of a newer deploy that is live but not yet loaded in this tab. */
export function getPendingBuildId(): string | null {
  return pendingBuildId;
}

/** Subscribe to "a newer build is live" notifications. Returns an unsubscribe. */
export function onNewBuildAvailable(fn: (buildId: string) => void): () => void {
  listeners.add(fn);
  if (pendingBuildId) fn(pendingBuildId);
  return () => listeners.delete(fn);
}

/** Removes any legacy service worker registration and cached responses. */
async function purgeWorkerCaches(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.allSettled(registrations.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.allSettled(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Non-fatal: cache purging is best effort.
  }
}

/** Hard reload onto the new build, guarded against reload loops. */
export function reloadToLatest(buildId = pendingBuildId ?? BUILD_ID): void {
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === buildId) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, buildId);
  } catch {
    // Storage blocked: still reload, just without loop protection.
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_v", buildId);
  window.location.replace(url.toString());
}

/** Reads the build id advertised by the currently deployed shell. */
async function fetchDeployedBuildId(): Promise<string | null> {
  const bust = `?_cb=${Date.now()}`;
  try {
    const res = await fetch(`/version.json${bust}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (res.ok) {
      const body = (await res.json()) as { buildId?: string };
      if (body?.buildId) return String(body.buildId);
    }
  } catch {
    // Fall through to the HTML shell.
  }
  try {
    const res = await fetch(`/${bust}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return null;
    const html = await res.text();
    return html.match(/name="build-id"\s+content="([^"]+)"/)?.[1] ?? null;
  } catch {
    return null;
  }
}

function announce(buildId: string): void {
  pendingBuildId = buildId;
  listeners.forEach((fn) => {
    try {
      fn(buildId);
    } catch {
      // A bad listener must not stall the guard.
    }
  });
  // If nobody is looking at the tab, swap builds silently.
  if (document.visibilityState === "hidden") {
    void purgeWorkerCaches().then(() => reloadToLatest(buildId));
  }
}

async function checkForNewBuild(force = false): Promise<void> {
  if (BUILD_ID === "dev") return;
  const now = Date.now();
  if (!force && now - lastCheckedAt < CHECK_INTERVAL_MS) return;
  lastCheckedAt = now;

  const deployed = await fetchDeployedBuildId();
  if (!deployed || deployed === "__BUILD_ID__" || deployed === BUILD_ID) return;
  if (deployed === pendingBuildId) return;
  await purgeWorkerCaches();
  announce(deployed);
}

/**
 * Stale route chunks are the loudest symptom of an old build: the HTML shell in
 * memory points at asset hashes that no longer exist. Recover by reloading.
 */
function isStaleChunkError(reason: unknown): boolean {
  const message =
    typeof reason === "string" ? reason : ((reason as { message?: string })?.message ?? "");
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(
    message,
  );
}

const STALE_RELOAD_COUNT_KEY = "gg-stale-reloads";
const MAX_STALE_RELOADS = 2;

function handleStaleChunk(): void {
  // Recovery must never become a reload loop: cap attempts per tab session.
  try {
    const seen = Number(sessionStorage.getItem(STALE_RELOAD_COUNT_KEY) ?? "0");
    if (seen >= MAX_STALE_RELOADS) return;
    sessionStorage.setItem(STALE_RELOAD_COUNT_KEY, String(seen + 1));
  } catch {
    // Storage blocked: fall through and reload once.
  }
  void purgeWorkerCaches().then(() => {
    const target = pendingBuildId ?? `stale-${Date.now()}`;
    reloadToLatest(target);
  });
}


/** Call once at app startup. */
export function initBuildVersionGuard(): void {
  if (typeof window === "undefined") return;

  void purgeWorkerCaches();

  try {
    const previous = localStorage.getItem(STORAGE_KEY);
    localStorage.setItem(STORAGE_KEY, BUILD_ID);
    if (previous && previous !== BUILD_ID) {
      // The new bundle is already running: clear the loop guard and stale caches.
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
      sessionStorage.removeItem(STALE_RELOAD_COUNT_KEY);
      void purgeWorkerCaches();
    }
  } catch {
    // Storage unavailable: version detection still works via polling.
  }

  window.addEventListener("focus", () => void checkForNewBuild());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkForNewBuild();
  });
  window.setInterval(() => void checkForNewBuild(true), POLL_INTERVAL_MS);

  // Vite fires this when a preloaded route chunk can no longer be fetched.
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    handleStaleChunk();
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (isStaleChunkError(event.reason)) handleStaleChunk();
  });
  window.addEventListener("error", (event) => {
    if (isStaleChunkError(event.error ?? event.message)) handleStaleChunk();
  });
}
